import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useProject } from "../../state/ProjectContext";

/**
 * Generic editable table used by every new domain tab. It reads/writes an
 * array at a given path on the project doc (e.g. `governance.crLog`) and
 * autosaves on blur — same pattern as every other tab.
 *
 * Props:
 *   sectionKey:   top-level key ("governance", "cutover" ...)
 *   listKey:      list key under that section (optional if data is the top-level array)
 *   columns:      [{ key, label, type?: "text"|"number"|"date"|"select", options?: string[], width?: string }]
 *   emptyRow:     () => object — factory for new row
 *   testidPrefix: string
 *   labelSingular:string
 */
export default function EditableTable({
  sectionKey, listKey, columns, emptyRow, testidPrefix, labelSingular = "row",
}) {
  const { doc, mutate, readOnly } = useProject();
  const [addingRow, setAddingRow] = useState(false);

  // Read the current list from the doc (defaulting to []).
  const rows = (listKey
    ? (doc?.[sectionKey]?.[listKey])
    : doc?.[sectionKey]) || [];

  const setList = (fn) => mutate(d => {
    const currSection = d[sectionKey] || {};
    const currList = listKey ? (currSection[listKey] || []) : (d[sectionKey] || []);
    const nextList = fn(currList);
    if (listKey) return { ...d, [sectionKey]: { ...currSection, [listKey]: nextList } };
    return { ...d, [sectionKey]: nextList };
  });

  const addRow = () => {
    const row = emptyRow();
    row.id = row.id || `${testidPrefix}-${Date.now().toString(36)}`;
    setList(list => [...list, row]);
    setAddingRow(false);
  };

  const updateCell = (idx, key, value) => {
    setList(list => list.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  };

  const removeRow = (idx) => setList(list => list.filter((_, i) => i !== idx));

  const renderCell = (col, r, idx) => {
    const v = r[col.key];
    if (readOnly) return <span className="text-sm text-zinc-800">{v || "—"}</span>;
    if (col.type === "select") {
      return (
        <select
          className="fld py-1 text-xs"
          value={v || ""}
          onChange={e => updateCell(idx, col.key, e.target.value)}
          data-testid={`${testidPrefix}-${idx}-${col.key}`}
        >
          <option value="">—</option>
          {(col.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <input
        type={col.type || "text"}
        className="fld py-1 text-xs"
        defaultValue={v || ""}
        onBlur={e => updateCell(idx, col.key, col.type === "number" ? Number(e.target.value) || 0 : e.target.value)}
        data-testid={`${testidPrefix}-${idx}-${col.key}`}
      />
    );
  };

  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full">
        <thead className="bg-zinc-50 border-b border-[var(--border)]">
          <tr>
            {columns.map(c => (
              <th key={c.key} className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500" style={{ width: c.width }}>
                {c.label}
              </th>
            ))}
            {!readOnly && <th className="px-3 py-2 w-10" />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length + 1} className="px-3 py-6 text-center text-sm text-zinc-400 italic">
              No {labelSingular}s yet. Add one below.
            </td></tr>
          ) : rows.map((r, idx) => (
            <tr key={r.id || idx} className="border-b border-[var(--border)] last:border-0 hover:bg-zinc-50">
              {columns.map(c => <td key={c.key} className="px-3 py-1.5 align-top">{renderCell(c, r, idx)}</td>)}
              {!readOnly && (
                <td className="px-2 py-1.5 align-top">
                  <button onClick={() => removeRow(idx)} className="btn btn-ghost btn-sm text-red-600" title="Delete row" data-testid={`${testidPrefix}-${idx}-delete`}>
                    <Trash2 size={12}/>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {!readOnly && (
        <div className="border-t border-[var(--border)] p-2 bg-zinc-50 flex justify-end">
          <button className="btn btn-primary btn-sm" onClick={addRow} data-testid={`${testidPrefix}-add`}>
            <Plus size={12}/> Add {labelSingular}
          </button>
        </div>
      )}
    </div>
  );
}
